// Port of ios/Sources/ViewModels/EmojiComposer.swift.
//
// The `:smile:` autocomplete's pure half, sibling of `MentionComposer`. Names come in as
// plain strings; the UI resolves them to characters via `src/core/generated/emojiCharacters.json`
// (the Apple `EmojiCharacters`).
//
// OFFSETS are UTF-16 code units (a `<textarea>`'s `selectionStart`), not the Swift file's
// Characters — see mentionComposer.ts. `apply` therefore advances the cursor by the inserted
// glyph's UTF-16 length ("🎉" is 2), which is what puts the caret after it in the browser.

export interface Active {
  /** Index of the opening ':' in the text. */
  start: number;
  query: string;
}

function isNameChar(c: string): boolean {
  return /^[A-Za-z0-9_+-]$/.test(c);
}

/**
 * The `:query` being typed at `cursor`, or null. Two characters minimum before the strip
 * appears — a bare ":" is far more often a colon than an emoji. The ':' must sit at the
 * start of a word: "10:30" is a time, not a query.
 */
export function active(text: string, cursor: number): Active | null {
  if (cursor > text.length) return null;
  let i = cursor - 1;
  while (i >= 0) {
    const c = text[i];
    if (c === ':') {
      if (i > 0 && !/^\s$/u.test(text[i - 1])) return null;
      const query = text.slice(i + 1, cursor);
      if (query.length < 2) return null;
      return { start: i, query };
    }
    if (!isNameChar(c)) return null;
    i -= 1;
  }
  return null;
}

function byCodeUnits(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Prefix matches first (alphabetical), then contains-matches, capped — the desktop's feel
 * without its full ranking machinery.
 */
export function filter(names: readonly string[], query: string, limit = 8): string[] {
  const needle = query.toLowerCase();
  const prefix = names.filter((n) => n.startsWith(needle)).sort(byCodeUnits);
  const contains = names.filter((n) => !n.startsWith(needle) && n.includes(needle)).sort(byCodeUnits);
  return [...prefix, ...contains].slice(0, limit);
}

export interface Applied {
  text: string;
  cursor: number;
}

/** Replaces the `:query` (from its ':') with `character`, cursor after it. */
export function apply(text: string, cursor: number, active: Active, character: string): Applied {
  const newText = text.slice(0, active.start) + character + text.slice(cursor);
  return { text: newText, cursor: active.start + character.length };
}
